// Forløbsvælger – deterministic recommendation logic + bilingual content.
// Single source of truth for the wizard. No AI, no PII.

// Answer category codes -> product-slug votes (central rule table)
const VOTES = {
  // Q1 – situation
  uklar_behov: { "lotus-afklaring": 2 },
  foran_loesladelse: { "lotus-72": 1, "lotus-180": 1 },
  ustabil_hverdag: { "lotus-stabil-90": 2 },
  samlet_forloeb: { "lotus-180": 2 },
  allerede_aktiv: { "lotus-fastholdelse": 2 },
  // Q2 – start speed
  start_akut: { "lotus-72": 2 },
  start_inden_for_uger: {},
  start_ikke_fastlagt: { "lotus-afklaring": 1 },
  // Q3 – primary purpose
  formaal_afdaekke: { "lotus-afklaring": 2 },
  formaal_tryg_overgang: { "lotus-72": 2 },
  formaal_stabilisering: { "lotus-stabil-90": 2 },
  formaal_langsigtet_arbejde: { "lotus-180": 2 },
  formaal_forebygge_frafald: { "lotus-fastholdelse": 2 },
  // Q4 – scope
  omfang_kortvarig: { "lotus-afklaring": 2 },
  omfang_intensiv_overgang: { "lotus-72": 2 },
  omfang_flere_ugentlige: { "lotus-stabil-90": 2 },
  omfang_laengere: { "lotus-180": 2 },
  omfang_ugentlig_opfoelgning: { "lotus-fastholdelse": 2 },
  omfang_uklart: { "lotus-afklaring": 1 },
};

const SLUGS = ["lotus-afklaring", "lotus-72", "lotus-stabil-90", "lotus-180", "lotus-fastholdelse"];

// answers: { q1, q2, q3, q4 } (category codes). Returns a deterministic result.
export function recommend(answers) {
  const scores = Object.fromEntries(SLUGS.map((s) => [s, 0]));
  const needsSummary = [];
  ["q1", "q2", "q3", "q4"].forEach((k) => {
    const v = answers[k];
    if (!v) return;
    needsSummary.push(v);
    const votes = VOTES[v] || {};
    Object.entries(votes).forEach(([slug, pts]) => {
      scores[slug] += pts;
    });
  });

  const ranked = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  const [topSlug, topScore] = ranked[0];
  const [secondSlug, secondScore] = ranked[1];

  const tie = topScore > 0 && topScore === secondScore;
  const weak = topScore < 3;
  const ambiguous = weak || tie;

  let recommended;
  let alternative = null;

  if (ambiguous) {
    recommended = "lotus-afklaring";
    const alt = ranked.find(([s, sc]) => s !== "lotus-afklaring" && sc > 0);
    alternative = alt ? alt[0] : null;
  } else {
    recommended = topSlug;
    if (secondScore > 0 && secondScore >= topScore - 1 && secondSlug !== recommended) {
      alternative = secondSlug;
    }
  }

  return { recommended, alternative, ambiguous, needsSummary, scores };
}

export const finderMeta = {
  da: {
    seoTitle: "Find det rette forløb | Lotusgruppen",
    seoDesc:
      "Få en vejledende anbefaling til et af Lotusgruppens fem forløb ud fra borgerens overordnede situation og støttebehov.",
    kicker: "Forløbsvælger",
    title: "Find det rette forløb",
    intro:
      "Svar på fire korte spørgsmål om borgerens overordnede situation, så giver vi en vejledende anbefaling til et af vores fem forløb. Det tager cirka ét minut.",
    legalStart:
      "Vælg kun overordnede beskrivelser af situationen. Indtast ikke navn, CPR-nummer eller andre oplysninger om en konkret borger.",
    legalResult:
      "Værktøjet giver en vejledende anbefaling og erstatter ikke kommunens myndighedsvurdering eller en individuel faglig afklaring.",
    startBtn: "Start forløbsvælger",
    step: "Trin",
    of: "af",
    back: "Tilbage",
    restart: "Start forfra",
    resultKicker: "Vejledende anbefaling",
    reasonLabel: "Hvorfor dette forløb?",
    durationLabel: "Varighed",
    intensityLabel: "Kontaktintensitet",
    purposeLabel: "Primært formål",
    deliverablesLabel: "Centrale leverancer",
    seeProduct: "Se forløbet",
    getPdf: "Hent produktark",
    alternativeLabel: "Muligt alternativ",
    alternativeExplain:
      "Afhængigt af borgerens situation kan dette forløb også være relevant. Forskellen drøftes bedst i dialog med Lotusgruppen.",
    primaryCta: "Drøft anbefalingen med Lotusgruppen",
    disclaimer:
      "Anbefalingen er vejledende. Det endelige forløb fastlægges i dialog med bestiller på baggrund af borgerens situation, støttebehov og den konkrete bestilling.",
    ambiguousReason:
      "Der er endnu ikke et tilstrækkeligt klart grundlag for at anbefale et længere forløb. Lotus Afklaring kan skabe overblik over behov, ressourcer, risici og forventet støtteintensitet.",
    reasons: {
      "lotus-afklaring":
        "Der er endnu ikke et tilstrækkeligt klart grundlag for et længere forløb. Lotus Afklaring skaber overblik over behov, ressourcer, risici og forventet støtteintensitet.",
      "lotus-72":
        "Borgeren står foran en løsladelse, hvor de første timer og døgn er afgørende. Lotus 72 sikrer en tryg og planlagt overgang fra institution til samfund.",
      "lotus-stabil-90":
        "Hverdagen er præget af ustabilitet. Lotus Stabil 90 skaber ro om bolig, økonomi og struktur som fundament for videre udvikling.",
      "lotus-180":
        "Der er behov for et samlet, længerevarende forløb. Lotus 180 følger borgeren fra løsladelse gennem stabilisering og udvikling mod arbejde.",
      "lotus-fastholdelse":
        "Borgeren er allerede i gang i praktik, uddannelse eller arbejde. Lotus Fastholdelse forebygger fravær, konflikt og frafald med tæt opfølgning.",
    },
    ctaSee: "Se forløbet",
  },
  en: {
    seoTitle: "Find the right pathway | Lotusgruppen",
    seoDesc:
      "Get a guiding recommendation for one of Lotusgruppen's five pathways based on the citizen's overall situation and support needs.",
    kicker: "Pathway finder",
    title: "Find the right pathway",
    intro:
      "Answer four short questions about the citizen's overall situation and we'll give a guiding recommendation for one of our five pathways. It takes about a minute.",
    legalStart:
      "Choose only overall descriptions of the situation. Do not enter a name, CPR number or other information about a specific citizen.",
    legalResult:
      "The tool provides a guiding recommendation and does not replace the municipality's authority assessment or an individual professional evaluation.",
    startBtn: "Start the pathway finder",
    step: "Step",
    of: "of",
    back: "Back",
    restart: "Start over",
    resultKicker: "Guiding recommendation",
    reasonLabel: "Why this pathway?",
    durationLabel: "Duration",
    intensityLabel: "Contact intensity",
    purposeLabel: "Primary purpose",
    deliverablesLabel: "Core deliverables",
    seeProduct: "View pathway",
    getPdf: "Download product sheet",
    alternativeLabel: "Possible alternative",
    alternativeExplain:
      "Depending on the citizen's situation, this pathway may also be relevant. The difference is best discussed with Lotusgruppen.",
    primaryCta: "Discuss the recommendation with Lotusgruppen",
    disclaimer:
      "The recommendation is guiding. The final pathway is determined in dialogue with the commissioner based on the citizen's situation, support needs and the specific order.",
    ambiguousReason:
      "There is not yet a sufficiently clear basis to recommend a longer pathway. Lotus Assessment can create an overview of needs, resources, risks and expected support intensity.",
    reasons: {
      "lotus-afklaring":
        "There is not yet a sufficiently clear basis for a longer pathway. Lotus Assessment creates an overview of needs, resources, risks and expected support intensity.",
      "lotus-72":
        "The citizen is facing release, where the first hours and days are decisive. Lotus 72 ensures a safe, planned transition from institution to society.",
      "lotus-stabil-90":
        "Everyday life is marked by instability. Lotus Stabil 90 creates calm around housing, finances and structure as a foundation for further development.",
      "lotus-180":
        "A complete, longer pathway is needed. Lotus 180 follows the citizen from release through stabilisation and development towards work.",
      "lotus-fastholdelse":
        "The citizen has already started a placement, education or job. Lotus Retention prevents absence, conflict and drop-out with close follow-up.",
    },
    ctaSee: "View pathway",
  },
};

export const questions = {
  da: [
    {
      code: "q1",
      q: "Hvilken situation beskriver bedst borgerens aktuelle behov?",
      options: [
        { value: "uklar_behov", label: "Behov og støtteintensitet er endnu uklare" },
        { value: "foran_loesladelse", label: "Borgeren står foran en løsladelse" },
        { value: "ustabil_hverdag", label: "Borgerens hverdag er præget af ustabilitet" },
        { value: "samlet_forloeb", label: "Behov for et samlet forløb fra løsladelse til beskæftigelse" },
        { value: "allerede_aktiv", label: "Borgeren er allerede startet i praktik, uddannelse eller arbejde" },
      ],
    },
    {
      code: "q2",
      q: "Hvor hurtigt er der behov for, at indsatsen starter?",
      options: [
        { value: "start_akut", label: "Akut eller inden for få dage" },
        { value: "start_inden_for_uger", label: "Inden for de kommende uger" },
        { value: "start_ikke_fastlagt", label: "Starttidspunktet er endnu ikke fastlagt" },
      ],
    },
    {
      code: "q3",
      q: "Hvad er det vigtigste formål lige nu?",
      options: [
        { value: "formaal_afdaekke", label: "At afdække behov, ressourcer og risici" },
        { value: "formaal_tryg_overgang", label: "At sikre en tryg overgang efter løsladelse" },
        { value: "formaal_stabilisering", label: "At skabe stabilitet omkring bolig, økonomi og hverdagsstruktur" },
        { value: "formaal_langsigtet_arbejde", label: "At arbejde langsigtet mod uddannelse eller beskæftigelse" },
        { value: "formaal_forebygge_frafald", label: "At forebygge fravær, konflikt eller frafald" },
      ],
    },
    {
      code: "q4",
      q: "Hvor omfattende forventes støtten at være?",
      options: [
        { value: "omfang_kortvarig", label: "Kortvarig afklaring" },
        { value: "omfang_intensiv_overgang", label: "Intensiv støtte i en overgang" },
        { value: "omfang_flere_ugentlige", label: "Flere ugentlige kontakter over en afgrænset periode" },
        { value: "omfang_laengere", label: "Et længere og sammenhængende udviklingsforløb" },
        { value: "omfang_ugentlig_opfoelgning", label: "Ugentlig opfølgning og hurtig indsats efter behov" },
        { value: "omfang_uklart", label: "Det er endnu uklart" },
      ],
    },
  ],
  en: [
    {
      code: "q1",
      q: "Which situation best describes the citizen's current needs?",
      options: [
        { value: "uklar_behov", label: "Needs and support intensity are still unclear" },
        { value: "foran_loesladelse", label: "The citizen is facing release" },
        { value: "ustabil_hverdag", label: "The citizen's everyday life is marked by instability" },
        { value: "samlet_forloeb", label: "A complete pathway from release to employment is needed" },
        { value: "allerede_aktiv", label: "The citizen has already started a placement, education or job" },
      ],
    },
    {
      code: "q2",
      q: "How quickly does the effort need to start?",
      options: [
        { value: "start_akut", label: "Urgently or within a few days" },
        { value: "start_inden_for_uger", label: "Within the coming weeks" },
        { value: "start_ikke_fastlagt", label: "The start time is not yet decided" },
      ],
    },
    {
      code: "q3",
      q: "What is the most important purpose right now?",
      options: [
        { value: "formaal_afdaekke", label: "To map needs, resources and risks" },
        { value: "formaal_tryg_overgang", label: "To ensure a safe transition after release" },
        { value: "formaal_stabilisering", label: "To create stability around housing, finances and daily structure" },
        { value: "formaal_langsigtet_arbejde", label: "To work long-term towards education or employment" },
        { value: "formaal_forebygge_frafald", label: "To prevent absence, conflict or drop-out" },
      ],
    },
    {
      code: "q4",
      q: "How extensive is the support expected to be?",
      options: [
        { value: "omfang_kortvarig", label: "A short assessment" },
        { value: "omfang_intensiv_overgang", label: "Intensive support during a transition" },
        { value: "omfang_flere_ugentlige", label: "Several weekly contacts over a limited period" },
        { value: "omfang_laengere", label: "A longer, coherent development pathway" },
        { value: "omfang_ugentlig_opfoelgning", label: "Weekly follow-up and fast action as needed" },
        { value: "omfang_uklart", label: "It is still unclear" },
      ],
    },
  ],
};
